import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { GiftCard } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { CustomerLink } from './customer-link';
import { GiftCardStatusBadge } from './gift-card-status-badge';

/** Column model of the gift cards table; `meta` ties columns to the list's sorts and filters. */
export function useGiftCardColumns(storeId: string) {
  const { t } = useTranslation('giftCards');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<GiftCard>>>(
    () => [
      {
        id: 'code',
        header: t('columns.code'),
        cell: ({ row }) => (
          <Link
            to="/s/$storeId/gift-cards/$giftCardId"
            params={{ storeId, giftCardId: row.original.id }}
            className="font-mono text-[13px] font-medium hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {t('masked', { last4: row.original.last4 })}
          </Link>
        ),
        meta: { label: t('columns.code'), lockVisibility: true },
        size: 120,
      },
      {
        id: 'balance',
        header: t('columns.balance'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            <span className="font-medium">{format.money(row.original.balance)}</span>
            <span className="text-muted-foreground">
              {' / '}
              {format.money(row.original.initial)}
            </span>
          </span>
        ),
        meta: { sortField: 'balance', filterKey: 'balance', align: 'end' },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <GiftCardStatusBadge status={row.original.status} />,
        meta: { filterKey: 'status' },
        size: 110,
      },
      {
        id: 'customer',
        header: t('columns.customer'),
        cell: ({ row }) => <CustomerLink storeId={storeId} customerId={row.original.customerId} />,
        meta: { label: t('columns.customer'), filterKey: 'customerId' },
      },
      {
        id: 'expiresAt',
        accessorKey: 'expiresAt',
        header: t('columns.expires'),
        cell: ({ row }) =>
          row.original.expiresAt ? (
            <span title={format.utc(row.original.expiresAt)}>
              {format.date(row.original.expiresAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('noExpiry')}</span>
          ),
        meta: { sortField: 'expiresAt', filterKey: 'expiresAt' },
        size: 130,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: t('columns.created'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.createdAt)}>
            {format.relative(row.original.createdAt)}
          </span>
        ),
        meta: { sortField: 'createdAt', filterKey: 'createdAt' },
        size: 130,
      },
    ],
    [format, storeId, t],
  );
}
